
import { useState, useEffect, useCallback } from 'react';

import { useRouter } from 'src/routes/hooks'

import { useCustomNavigate } from 'src/hooks/use-custom-navigate';

import { jwtDecode } from 'src/utils/isValidToken';

import { AxiosHttpClient } from 'src/http';
import { API_URL, NODE_ENV } from 'src/config-global';
import { useUserStore } from 'src/stores/user/user.store';
import { AuthRepository } from 'src/repositories/auth/auth.repository';
import { useCustomContent } from 'src/stores/custom-content/custom-content.store';
import { AnalyticsRepository } from 'src/repositories/analytics/analytics.repository';

import { SplashScreen } from 'src/components/loading-screen';

import { useAuthStore } from '../auth.store';

type Props = {
  children: React.ReactNode;
};

export default function AuthGuard({ children }: Props) {
  const { isLoading } = useAuthStore();

  return <>{isLoading ? <SplashScreen /> : <Container>{children}</Container>}</>;
}

function Container({ children }: Props) {
  const router = useRouter();
  const navigate = useCustomNavigate()

  // const api = new AxiosHttpClient(API_URL);
  // const { user } = useUserStore();
  // const repository = new UserRepository(api);

  const {
    isAuthenticated,
    accessToken,
    refreshToken,
    // clearSession,
    setIsAuthenticated,
  } = useAuthStore();

  const { setUserData, setRDSummitVersion } = useUserStore((state) => state)

  const { businessUnity, eventData } = useCustomContent((state) => state)
  const [checked, setChecked] = useState(false);

  const api = new AxiosHttpClient(API_URL);
  // const authApi = new AxiosHttpClient(API_URL);
  // const api = new AxiosHttpClient('http://localhost:2020');

  const repository = new AnalyticsRepository(api);
  const authRepository = new AuthRepository(api);

  useEffect(() => {
    if (window.location.pathname.includes('auth/login')) {
      return;
    }
    const sendLog = async () => {
      let tokenData;
      if (accessToken) {
        tokenData = jwtDecode(accessToken);
      }
      const logData = {
        message: 'LP Events user accessing application',
        tag: 'lp-events-initial-page-access',
        env: NODE_ENV === 'production' ? 'production' : 'sandbox',
        level: 'info',
        data: tokenData?.data || {},
      };
      await repository.sendLog([logData]);
    };
    if (isAuthenticated) {
      sendLog();
    }
  }, []);

  const initializeAuth = async () => {
    if (businessUnity?.id && eventData?._id) {
      const response = await authRepository.verifyEventAccess(businessUnity.id, eventData._id)
      if (response.isLeft()) {
        setIsAuthenticated(false)
      } else if (accessToken && refreshToken) {
        setUserData(jwtDecode(refreshToken).data)
        setIsAuthenticated(true);
        setRDSummitVersion(response?.value?.rdSummitVersion);
      } else {
        setIsAuthenticated(false);
      }
    }
  };

  const check = useCallback(() => {
    if (!isAuthenticated) {
      navigate('/auth/login')
    } else {
      setChecked(true);
    }
  }, [isAuthenticated, router]);

  const auth = async () => {
    await initializeAuth();
    check();
  };

  useEffect(() => {
    auth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, router, businessUnity?.id, eventData?._id]);

  if (!checked) {
    return null;
  }

  return <>{isAuthenticated ? children : <SplashScreen />}</>;
}
